import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {  tapResponse } from '@ngrx/operators';
import { pipe, withLatestFrom, zip } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { ZohoSettingsService } from '../zoho-setup/zoho-settings.service';
import { AccountConfigType } from '../../models/account-config-type';
import { ConfigService } from '../../shared/services/config.service';
import { GenericAccount } from '../../models/GenericAccount';
import { TaxRate } from '../../models/tax-rate';
import { SageSettingsService } from '../sage-setup/sage-settings.service';
import { GenericTaxRate } from '../../models/generic-tax-rate';
import { BrcSettingsService } from '../brc-setup/brc-settings.service';

type IntegrationType = 'zoho' | 'sage' | 'brc';

export const INTEGRATION_IJ = new InjectionToken('INTEGRATION_IJ');

interface AccountSetupDialogState {
  integration: IntegrationType;
  loadingAccounts: boolean;
  loadingTaxRates: boolean;
  accounts: GenericAccount[];
  remoteTaxRates: GenericTaxRate[];
  configuredAccounts: {
    [key: string]: string;
  };
}

export interface TaxRateMapping {
  sequence: number;
  name: string;
  reference: string;
  value: number;
  type: string;
}

const hlAccounts = [
  AccountConfigType.HIRE_SALES_REVENUE_CODE,
  AccountConfigType.DELIVERY_REVENUE_CODE,
  AccountConfigType.CONSUMABLE_SALES_REVENUE_CODE,
  AccountConfigType.RETAIL_PURCHASES_CODE,
  AccountConfigType.RETAIL_SALES_REVENUE_CODE,
  AccountConfigType.CARD_PAYMENTS,
  AccountConfigType.BANK_TRANSFER_PAYMENTS
];

const initialState: AccountSetupDialogState = {
  integration: 'zoho' as IntegrationType,
  loadingAccounts: true,
  loadingTaxRates: true,
  accounts: [],
  remoteTaxRates: [],
  configuredAccounts: {}
};

@Injectable()
export class AccountSetupDialogStore extends ComponentStore<AccountSetupDialogState> {
  constructor(
    private zohoAccountsService: ZohoSettingsService,
    private sageSettingsService: SageSettingsService,
    private brcSettingsService: BrcSettingsService,
    private configService: ConfigService,
    @Inject(INTEGRATION_IJ) private integration: IntegrationType
  ) {
    super({ ...initialState, integration });
  }

  readonly integration$ = this.select((state) => state.integration);
  readonly loadingAccounts$ = this.select((state) => state.loadingAccounts);
  readonly loadingTaxRates$ = this.select((state) => state.loadingTaxRates);
  readonly accounts$ = this.select((state) => state.accounts);
  readonly taxRates$ = this.select((state) => state.remoteTaxRates);
  readonly taxRateMappings$ = this.configService.taxRates.pipe(
    withLatestFrom(this.integration$),
    map(([localTaxRates, integration]) =>
      this.localTaxRateToMapping(localTaxRates, integration)
    )
  );
  readonly optionsByTaxType$ = this.taxRates$.pipe(
    withLatestFrom(this.integration$),
    mergeMap(([taxRates, integration]) =>
      this.getTaxConfigurationValuesGroupedByType().pipe(
        map((taxConfig) =>
          this.groupTaxOptionsByValue(taxConfig, taxRates, integration)
        )
      )
    )
  );

  readonly hlAccountsToBeMapped$ = this.integration$.pipe(
    withLatestFrom(
      this.configService.userConfig.pipe(
        map((config) => {
          const defaultCurrency = config.currency ?? 'GBP';
          if (config.additional_currencies) {
            return [defaultCurrency, ...config.additional_currencies.split(',')];
          }
          return [defaultCurrency];
        })
      )
    ),
    map(([integration, currency]) =>
      this.getAccountsToBeMapped(integration, currency)
    ),
    map((accounts) => this.sortAccountsByType(accounts))
  );

  readonly configuredAccounts$ = this.select(
    (state) => state.configuredAccounts
  );

  readonly setIntegration = this.updater(
    (state, integration: 'zoho' | 'sage' | 'brc') => {
      return {
        ...state,
        integration
      };
    }
  );

  readonly getAccounts = this.effect<void>(
    pipe(
      tap(() => this.patchState({ loadingAccounts: true })),
      withLatestFrom(this.integration$),
      mergeMap(([_, integration]) =>
        this.getAccountsAndMappingsForIntegration(integration)
      ),
      tapResponse(
        ([accounts, mappings]) =>
          this.patchState({ accounts, configuredAccounts: mappings }),
        (error) => console.log(error)
      ),
      tap(() => this.patchState({ loadingAccounts: false }))
    )
  );

  readonly saveAccountMappings = this.effect<{
    mappings: { [key: string]: string };
    onSuccess: () => void;
  }>(
    pipe(
      tap(() => this.patchState({ loadingAccounts: true })),
      withLatestFrom(this.integration$),
      mergeMap(([{ mappings, onSuccess }, integration]) =>
        this.saveAccountMappingsForIntegration(mappings, integration).pipe(
          tapResponse(
            () => {
              this.patchState({ loadingAccounts: false });
              onSuccess();
            },
            (error) => console.log(error)
          )
        )
      )
    )
  );

  readonly saveTaxMappings = this.effect<{
    mappings: TaxRateMapping[];
    onSuccess: () => void;
  }>(
    pipe(
      tap(() => this.patchState({ loadingTaxRates: true })),
      withLatestFrom(this.integration$),
      mergeMap(([{ mappings, onSuccess }, integration]) =>
        this.saveTaxMappingsForIntegration(mappings, integration).pipe(
          tapResponse(
            () => {
              this.patchState({ loadingTaxRates: false });
              onSuccess();
            },
            (error) => console.log(error)
          )
        )
      )
    )
  );

  readonly getTaxRates = this.effect<void>(
    pipe(
      tap(() => this.patchState({ loadingTaxRates: true })),
      withLatestFrom(this.integration$),
      mergeMap(([_, integration]) =>
        this.getTaxRatesForIntegration(integration)
      ),
      tapResponse(
        (taxRates) => this.patchState({ remoteTaxRates: taxRates }),
        (error) => console.log(error)
      ),
      mergeMap(() =>
        this.configService.taxRates.pipe(filter((taxRates) => !!taxRates))
      ),
      tap(() => this.patchState({ loadingTaxRates: false }))
    )
  );

  private getAccountsAndMappingsForIntegration(integration: IntegrationType) {
    // TODO: Implement Sage
    if (integration === 'zoho') {
      return zip(
        this.zohoAccountsService
          .getAccounts()
          .pipe(
            map((accounts) =>
              accounts.sort((a, b) => a.name.localeCompare(b.name))
            )
          ),
        this.zohoAccountsService.getConfiguredAccounts()
      );
    }
    if (integration === 'sage') {
      return zip(
        this.sageSettingsService
          .getAccounts()
          .pipe(
            map((accounts) =>
              accounts.sort((a, b) => a.name.localeCompare(b.name))
            )
          ),
        this.sageSettingsService.getConfiguredAccounts()
      );
    }
    if (integration === 'brc') {
      return zip(
        this.brcSettingsService
          .getAccounts()
          .pipe(
            map((accounts) =>
              accounts.sort((a, b) => a.name.localeCompare(b.name))
            )
          ),
        this.brcSettingsService.getConfiguredAccounts()
      );
    }
    throw new Error('Invalid integration');
  }

  private getAccountsToBeMapped(integration, currencies: string[]) {
    // TODO: Implement Sage
    const currencyAccounts = currencies.flatMap((currency) =>
       [
        AccountConfigType.CASH_PAYMENTS_PREFIX + currency,
        AccountConfigType.CHEQUE_PAYMENTS_PREFIX + currency
      ]
    );
    return [
      ...hlAccounts,
      ...currencyAccounts
    ];
  }

  private sortAccountsByType(accounts: string[]) {
    const accountsByType = {
      revenue: [],
      payment: [],
      expenses: []
    };
    const revenueAccounts = [
      AccountConfigType.DELIVERY_REVENUE_CODE,
      AccountConfigType.HIRE_SALES_REVENUE_CODE,
      AccountConfigType.CONSUMABLE_SALES_REVENUE_CODE,
      AccountConfigType.REPAIR_LABOUR_REVENUE_CODE,
      AccountConfigType.HIRE_EQUIPMENT_SALES_REVENUE_CODE,
      AccountConfigType.RETAIL_SALES_REVENUE_CODE,
      AccountConfigType.REPAIR_PARTS_REVENUE_CODE
    ];
    const expneseAccounts = [
      AccountConfigType.HIRE_EQUIPMENT_PURCHASES_CODE,
      AccountConfigType.CONSUMABLE_PURCHASE_CODE,
      AccountConfigType.RETAIL_PURCHASES_CODE
    ];
    return accounts.reduce((acc, cur) => {
      if (revenueAccounts.includes(cur)) {
        acc.revenue.push(cur);
      } else if (expneseAccounts.includes(cur)) {
        acc.expenses.push(cur);
      } else {
        acc.payment.push(cur);
      }
      return acc;
    }, accountsByType);
  }

  private getTaxConfigurationValuesGroupedByType() {
    return this.configService.taxRates.pipe(
      map((taxRates) => {
        return taxRates.reduce(
          (acc, cur) => {
            if (!acc[cur.type]) {
              acc[cur.type] = [];
            }
            acc[cur.type].push(cur.value * 100);
            return acc;
          },
          {} as { [key: string]: number[] }
        );
      })
    );
  }

  private groupTaxOptionsByValue(
    taxConfig: { [key: string]: number[] },
    taxRates: any,
    integration: any
  ) {
    return Object.entries(taxConfig).reduce((acc, [key, value]) => {
      acc[key] = taxRates.filter((taxRate) =>
        value.includes(taxRate.tax_percentage)
      );
      return acc;
    }, {});
  }

  private localTaxRateToMapping(
    localTaxRates: TaxRate[],
    integration: string
  ): TaxRateMapping[] {
    return localTaxRates.map((taxRate) => {
      const reference =
        integration === 'sage'
          ? parseInt(taxRate[`accounts_reference_${integration}`], 10)
          : taxRate[`accounts_reference_${integration}`];
      return {
        name: taxRate.name,
        value: taxRate.value,
        reference,
        type: taxRate.type,
        sequence: taxRate.config_sequence
      };
    });
  }

  private saveAccountMappingsForIntegration(
    mappings: any,
    integration: IntegrationType
  ) {
    if (integration === 'zoho') {
      return this.zohoAccountsService.saveAccountMappings(mappings);
    }
    if (integration === 'sage') {
      return this.sageSettingsService.saveAccountMappings(mappings);
    }
    if (integration === 'brc') {
      return this.brcSettingsService.saveAccountMappings(mappings);
    }
    throw new Error('Invalid integration');
  }

  private getTaxRatesForIntegration(integration: IntegrationType) {
    if (integration === 'zoho') {
      return this.zohoAccountsService.getTaxRates();
    }
    if (integration === 'sage') {
      return this.sageSettingsService.getTaxRates();
    }
    if (integration === 'brc') {
      return this.brcSettingsService.getTaxRates();
    }
    throw new Error('Invalid integration');
  }

  private saveTaxMappingsForIntegration(mappings: any, integration: any) {
    if (integration === 'zoho') {
      return this.zohoAccountsService.saveTaxMappings(mappings);
    }
    if (integration === 'sage') {
      return this.sageSettingsService.saveTaxMappings(mappings);
    }
    if (integration === 'brc') {
      return this.brcSettingsService.saveTaxMappings(mappings);
    }
    throw new Error('Invalid integration');
  }
}
